import { useCallback, useState } from 'react';

import type { TFunction } from '@adsk/alloy-react-localization';

import { useTranslation } from '../i18n';
import useAutocompleteTabNavigation from './useAutocompleteTabNavigation';
import type { GenericItem } from '../types';

const getPlaceholder = ({
  t,
  hasTokens,
  isOpen,
  isInputFocused,
  placeholderOverride,
  searchPlaceholderOverride,
}: {
  t: TFunction;
  hasTokens?: boolean;
  isOpen?: boolean;
  isInputFocused?: boolean;
  placeholderOverride?: string;
  searchPlaceholderOverride?: string;
}) => {
  if (hasTokens) {
    return '';
  }

  const isSearching = isOpen || isInputFocused;

  if (searchPlaceholderOverride) {
    return isSearching
      ? searchPlaceholderOverride
      : placeholderOverride || t('SELECT');
  }

  if (placeholderOverride) {
    return placeholderOverride;
  }

  return isSearching ? t('SEARCH') : t('SELECT');
};

export default ({
  placeholderProp,
  searchPlaceholderProp,
  hasTokens,
  isOpen,
  disabled,
  openMenu,
  selectedItem,
  selectedItems,
}: {
  placeholderProp?: string;
  searchPlaceholderProp?: string;
  hasTokens?: boolean;
  isOpen: boolean;
  disabled?: boolean;
  openMenu?: () => void;
  selectedItem?: GenericItem | null;
  selectedItems?: GenericItem[] | null;
}) => {
  const { t } = useTranslation();
  const [isInputFocused, setIsInputFocused] = useState(false);

  const placeholder = getPlaceholder({
    t,
    hasTokens,
    isOpen,
    isInputFocused,
    placeholderOverride: placeholderProp,
    searchPlaceholderOverride: searchPlaceholderProp,
  });

  const onFocus = useCallback(() => setIsInputFocused(true), []);
  const onBlur = useCallback(() => setIsInputFocused(false), []);

  const onContainerClick = useCallback(() => {
    !isOpen && !disabled && openMenu?.();
  }, [disabled, isOpen, openMenu]);

  const hasSelectedValue = !!(
    selectedItem ||
    (selectedItems && selectedItems.length > 0)
  );
  const shouldBeFocusable = !isOpen && hasSelectedValue;

  const tabNavigationProps = useAutocompleteTabNavigation({
    shouldBeFocusable,
    openMenu,
  });

  return {
    containerProps: {
      isOpen,
      isAutocomplete: true,
      disabled,
      onClick: onContainerClick,
      'data-dropdown-control-container': 'true',
      ...tabNavigationProps,
    },
    searchInputProps: {
      isOpen,
      disabled,
      placeholder,
      onFocus,
      onBlur,
      showIcon: !hasTokens && (isOpen || isInputFocused),
    },
  };
};
